// /admin/companies — Admin only. No DELETE in V1 per spec G-18.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { companiesApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import { useUrlState } from '@/utils/useUrlState';
import {
  EmptyState,
  ErrorBanner,
  Pagination,
  Skeleton,
} from '@/components/primitives';
import { Modal } from '@/components/ModalHost';
import { useToast } from '@/components/ToastHost';
import { mapApiErrorToFormErrors } from '@/utils/formErrors';
import { ApiError } from '@/types/errors';
import type { Company } from '@/types/domain';

export function CompaniesAdminPage() {
  const url = useUrlState();
  const h1Ref = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => h1Ref.current?.focus(), []);
  const cursor = url.get('cursor');

  const queryArgs = useMemo(
    () => ({ cursor: cursor ?? undefined }),
    [cursor],
  );
  const list = useQuery({
    queryKey: qk.companies(queryArgs),
    queryFn: () => companiesApi.list(queryArgs),
  });

  const [editorOpen, setEditorOpen] = useState<{
    mode: 'create' | 'edit';
    company?: Company;
  } | null>(null);

  return (
    <div>
      <header className="page-header">
        <h1 ref={h1Ref} tabIndex={-1}>
          Companies
        </h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditorOpen({ mode: 'create' })}
        >
          Add company
        </button>
      </header>

      {list.isPending ? (
        <Skeleton rows={5} height="32px" />
      ) : list.isError ? (
        <ErrorBanner
          message="Couldn't load companies."
          onRetry={() => void list.refetch()}
        />
      ) : (list.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          message="No companies yet."
          action={
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setEditorOpen({ mode: 'create' })}
            >
              Add company
            </button>
          }
        />
      ) : (
        <>
          <table className="data-table">
            <caption className="visually-hidden">Companies</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.data.map((c) => (
                <tr key={c.id}>
                  <th scope="row">{c.name}</th>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => setEditorOpen({ mode: 'edit', company: c })}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            nextCursor={list.data!.next_cursor ?? null}
            loading={list.isFetching}
            onNext={() => {
              if (list.data?.next_cursor) url.setCursor(list.data.next_cursor);
            }}
          />
        </>
      )}

      {editorOpen ? (
        <CompanyEditor
          mode={editorOpen.mode}
          company={editorOpen.company ?? null}
          onClose={() => setEditorOpen(null)}
        />
      ) : null}
    </div>
  );
}

interface EditorProps {
  mode: 'create' | 'edit';
  company: Company | null;
  onClose: () => void;
}

interface CompanyForm {
  name: string;
}

function CompanyEditor({ mode, company, onClose }: EditorProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const defaults: CompanyForm = useMemo(
    () => ({ name: company?.name ?? '' }),
    [company],
  );
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting, dirtyFields, isDirty },
  } = useForm<CompanyForm>({ defaultValues: defaults });

  const createM = useMutation({
    mutationFn: (body: CompanyForm) =>
      companiesApi.create({ name: body.name.trim() }),
  });
  const patchM = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CompanyForm }) =>
      companiesApi.update(id, { name: body.name.trim() }),
  });

  const onSubmit: SubmitHandler<CompanyForm> = async (values) => {
    try {
      if (mode === 'edit' && company) {
        if (!dirtyFields.name) {
          onClose();
          return;
        }
        await patchM.mutateAsync({ id: company.id, body: values });
        toast.show({ severity: 'success', message: 'Company saved.' });
      } else {
        await createM.mutateAsync(values);
        toast.show({ severity: 'success', message: 'Company added.' });
      }
      void qc.invalidateQueries({ queryKey: ['companies'] });
      onClose();
    } catch (err) {
      const mapped = mapApiErrorToFormErrors(err, ['name']);
      if (mapped.fieldErrors['name']) {
        setError('name', { type: 'server', message: mapped.fieldErrors['name'] });
      }
      if (
        err instanceof ApiError &&
        err.code === 'duplicate_identity'
      ) {
        setError('name', {
          type: 'server',
          message: 'A company with this name already exists.',
        });
      } else if (mapped.formMessage) {
        toast.show({ severity: 'error', message: mapped.formMessage });
      }
    }
  };

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={mode === 'create' ? 'Add company' : `Edit ${company?.name ?? 'company'}`}
      role="dialog"
      canClose={() => !isDirty || window.confirm('Discard unsaved changes?')}
    >
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="form-field">
          <label htmlFor="co-name">
            Name<span className="form-required">*</span>
          </label>
          <input
            id="co-name"
            disabled={isSubmitting}
            {...register('name', {
              required: 'Name is required.',
              validate: (v) => v.trim().length > 0 || 'Name is required.',
            })}
          />
          {errors.name ? (
            <span className="form-error">{errors.name.message}</span>
          ) : null}
        </div>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : mode === 'edit' ? 'Save' : 'Add company'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
